/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import config from 'js/app/config';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, Link, PasswordField, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import {
  RECAPTCHA_ACTIONS,
  SIGNUP_REDESIGN_RECAPTCHA_ID,
  PASSWORD_CONSTRAINT as isPasswordValid,
} from 'bundles/authentication/constants';
import * as actions from 'bundles/authentication/shared/actions';
import { useRecaptcha } from 'bundles/authentication/shared/hooks/useRecaptcha';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  learnerEmail: string;
  firstName: string;
  lastName: string;
  handleNextSection: (isNewSignup: boolean) => void;
  handleSubmit: () => void;
};

const styles = {
  primaryButton: css`
    margin-right: var(--cds-spacing-200);
  `,
  reCAPTCHA: css`
    display: none;
  `,
  passwordField: css`
    margin-bottom: var(--cds-spacing-150);

    label {
      margin: 0;
    }
  `,
};

export const SignupSection = (props: Props): React.ReactElement => {
  const { learnerEmail, firstName, lastName, handleNextSection, handleSubmit } = props;

  const [password, setPassword] = useState<string>('');
  const [hasFormError, setHasFormError] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');

  const recaptcha = useRecaptcha(SIGNUP_REDESIGN_RECAPTCHA_ID, { action: RECAPTCHA_ACTIONS.signup });
  const track = useTracker();

  useEffect(() => {
    const error = isPasswordValid(password);

    if (error) {
      setPasswordError(error);
    } else {
      setPasswordError('');
    }
  }, [password]);

  const handleCreateAccount = async () => {
    if (passwordError) {
      setHasFormError(true);
    } else {
      const name = `${firstName?.trim()} ${lastName?.trim()}`;
      const token = await recaptcha?.getResponse();

      try {
        // API call to create a new account
        await actions.signup({ email: learnerEmail, name, password, token });
        Instrumentation.register();
        track('interact_eoi', {
          eoiInteraction: 'signup',
          eoiSection: 'signup_section',
          pageSection: {
            sectionName: 'eoi',
            subindex: 1,
          },
        });

        // Resubmit the EOI again now that the user has a CAUTH cookie
        handleSubmit();
        handleNextSection(true);
        track('submit_eoi', {
          eoiStatus: 'success',
          eoiSection: 'signup_section',
          pageSection: {
            sectionName: 'eoi',
            subindex: 1,
          },
        });
      } catch (error) {
        track('submit_eoi', {
          eoiStatus: 'api_failure',
          eoiSection: 'signup_section',
          pageSection: {
            sectionName: 'eoi',
            subindex: 1,
          },
        });
        recaptcha?.reset();
      }
    }
  };

  const handleSkip = () => {
    track('submit_eoi', {
      eoiStatus: 'skip',
      eoiSection: 'signup_section',
      pageSection: {
        sectionName: 'eoi',
        subindex: 1,
      },
    });
    handleNextSection(false);
  };

  const { HeadingGroup, Content, Actions } = Dialog;

  return (
    <>
      <HeadingGroup
        supportText={_t(
          'By creating a Coursera account you can keep better track of your progress through your Degrees journey!'
        )}
      >
        {_t('Lastly, would you like to create a Coursera account?')}
      </HeadingGroup>
      <Content>
        <div css={styles.reCAPTCHA} id={SIGNUP_REDESIGN_RECAPTCHA_ID} data-testid="recaptcha-signup" />

        <PasswordField
          label={_t('Password')}
          fullWidth
          onChange={(e) => setPassword(e.target.value)}
          validationStatus={hasFormError && !!passwordError ? 'error' : undefined}
          validationLabel={passwordError}
          value={password}
          supportText={_t('Between 8 and 72 characters')}
          css={styles.passwordField}
        />

        <Typography2 component="p" variant="bodySecondary" color="supportText">
          <FormattedMessage
            message={_t(
              'By signing up to create an account I acknowledge {privacyNotice} and accept {termsOfService}.'
            )}
            privacyNotice={<Link href={`${config.url.base}about/privacy`}>{_t("Coursera's Privacy Notice")}</Link>}
            termsOfService={<Link href={`${config.url.base}about/terms`}>{_t("Coursera's Terms of Use")}</Link>}
          />
        </Typography2>
      </Content>
      <Actions>
        <Button onClick={handleCreateAccount} css={styles.primaryButton}>
          {_t('Create account')}
        </Button>

        <Button onClick={handleSkip} variant="secondary">
          {_t('No thanks')}
        </Button>
      </Actions>
    </>
  );
};

export default SignupSection;
